import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import Reveal from "../Reveal";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

export default function ContactSection() {
  const { t, lang } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", type: "general" });
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => {
      const detail = e.detail || {};
      if (detail.type === "docs") {
        const titles = t.docs.items.map((it) => it.title);
        const which = titles[detail.index] || "";
        setForm((f) => ({
          ...f,
          type: "docs",
          message: lang === "es"
            ? `Solicito acceso a: ${which}.`
            : `Requesting access to: ${which}.`,
        }));
      } else if (detail.type === "farm") {
        setForm((f) => ({
          ...f,
          type: "farm",
          message: lang === "es"
            ? "Tengo una finca de regadío que cumple los criterios (250-400 ha, agua acreditable, suelo apto). Aporto detalles del activo a continuación:\n\n"
            : lang === "fr"
            ? "J'ai une ferme irriguée qui répond aux critères (250-400 ha, eau attestable, sol apte). Détails de l'actif ci-dessous :\n\n"
            : "I have an irrigated farm that meets the criteria (250-400 ha, accredited water, suitable soil). Asset details below:\n\n",
        }));
        document.querySelector("[data-testid=contact-message]")?.focus();
      } else if (detail.type === "investor") {
        setForm((f) => ({
          ...f,
          type: "investor",
          message: lang === "es"
            ? "Estoy interesado en recibir información para inversores y partners de Agrovalor.\n\n"
            : lang === "fr"
            ? "Je souhaite recevoir l'information pour investisseurs et partenaires d'Agrovalor.\n\n"
            : "I'm interested in receiving information for investors and partners of Agrovalor.\n\n",
        }));
        document.querySelector("[data-testid=contact-message]")?.focus();
      }
    };
    window.addEventListener("agro:contact-prefill", onPrefill);
    return () => window.removeEventListener("agro:contact-prefill", onPrefill);
  }, [lang, t]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!gdprAccepted) {
      toast.error(c.gdprRequired);
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", { ...form, locale: lang });
      setDone(true);
      toast.success(c.success);
      setForm({ name: "", email: "", phone: "", message: "", type: form.type });
      setGdprAccepted(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || c.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 sm:py-32 bg-olive text-ivory">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <Reveal>
          <div data-testid="farm-cta-banner" className="border border-terracotta/40 bg-terracotta/[0.08] p-8 sm:p-12 mb-20 grid grid-cols-12 gap-y-6 gap-x-8 items-start">
            <div className="col-span-12 lg:col-span-3 flex items-start gap-4">
              <span className="block h-px w-12 bg-terracotta mt-3" aria-hidden />
              <span className="eyebrow text-terracotta">{c.farmCtaEyebrow}</span>
            </div>
            <div className="col-span-12 lg:col-span-9 flex flex-col gap-5">
              <p className="font-serif text-2xl sm:text-3xl text-ivory leading-snug">
                {c.farmCtaTitle}
              </p>
              <p className="text-base text-ivory/80 leading-relaxed max-w-2xl">
                {c.farmCtaBody}
              </p>
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("agro:contact-prefill", { detail: { type: "farm" } }));
                  document.querySelector("[data-testid=contact-form]")?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                data-testid="farm-cta-button"
                className="self-start bg-terracotta text-ivory px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase hover:tracking-[0.32em] transition-all duration-500"
              >
                {c.farmCtaButton}
              </button>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-12 gap-y-12 gap-x-6">
        <div className="col-span-12 lg:col-span-5">
          <Reveal className="flex items-start gap-4 mb-6">
            <span className="block h-px w-12 bg-terracotta mt-3" aria-hidden />
            <span className="eyebrow">{c.eyebrow}</span>
          </Reveal>
          <Reveal as="h2" className="h-section mb-6">
            {c.title}
          </Reveal>
          <Reveal>
            <p className="text-ivory/80 leading-relaxed text-lg max-w-md mb-12">{c.intro}</p>
          </Reveal>
          <Reveal>
            <div className="flex flex-col gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-ivory/70">
              <a href={`mailto:${c.email}`} className="link-underline">{c.email}</a>
              <span>{c.company}</span>
            </div>
          </Reveal>
        </div>

        <Reveal className="col-span-12 lg:col-span-6 lg:col-start-7">
          {done ? (
            <div data-testid="contact-success" className="border border-ivory/20 p-10">
              <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-terracotta mb-4">✓ ok</div>
              <p className="font-display text-3xl">{c.success}</p>
              <button onClick={() => setDone(false)} className="mt-8 font-mono text-[11px] tracking-[0.28em] uppercase border border-ivory/40 px-5 py-3 hover:bg-ivory hover:text-olive transition">
                ↻
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="flex flex-col gap-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <div className="flex flex-col gap-1 py-3">
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/60">{c.nameLabel}</label>
                  <input required value={form.name} onChange={onChange("name")} data-testid="contact-name" className="input-editorial on-dark" />
                </div>
                <div className="flex flex-col gap-1 py-3">
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/60">{c.emailLabel}</label>
                  <input required type="email" value={form.email} onChange={onChange("email")} data-testid="contact-email" className="input-editorial on-dark" />
                </div>
                <div className="flex flex-col gap-1 py-3">
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/60">{c.phoneLabel}</label>
                  <input value={form.phone} onChange={onChange("phone")} data-testid="contact-phone" className="input-editorial on-dark" />
                </div>
                <div className="flex flex-col gap-1 py-3">
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/60">{c.typeLabel}</label>
                  <select value={form.type} onChange={onChange("type")} data-testid="contact-type" className="input-editorial on-dark bg-olive">
                    <option value="general">{c.typeGeneral}</option>
                    <option value="farm">{c.typeFarm}</option>
                    <option value="investor">{c.typeInvestor}</option>
                    <option value="buyer">{c.typeBuyer}</option>
                    <option value="provider">{c.typeProvider}</option>
                    <option value="collab">{c.typeCollab}</option>
                    <option value="docs">{c.typeDocs}</option>
                    <option value="other">{c.typeOther}</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1 py-3">
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/60">{c.messageLabel}</label>
                <textarea required rows={4} value={form.message} onChange={onChange("message")} data-testid="contact-message" className="input-editorial on-dark resize-none" />
              </div>

              <label className="flex items-start gap-3 mt-6 cursor-pointer group" data-testid="contact-gdpr-label">
                <input
                  type="checkbox"
                  required
                  checked={gdprAccepted}
                  onChange={(e) => setGdprAccepted(e.target.checked)}
                  data-testid="contact-gdpr"
                  className="mt-1 h-4 w-4 accent-terracotta shrink-0 cursor-pointer"
                />
                <span className="text-sm text-ivory/85 leading-snug">
                  {c.gdprLabel.split("política de privacidad").length > 1 ? (
                    <>
                      He leído y acepto la{" "}
                      <Link to="/legal/privacidad" target="_blank" className="underline text-terracotta hover:text-ivory">
                        política de privacidad
                      </Link>
                      .
                    </>
                  ) : c.gdprLabel.split("privacy policy").length > 1 ? (
                    <>
                      I have read and accept the{" "}
                      <Link to="/legal/privacidad" target="_blank" className="underline text-terracotta hover:text-ivory">
                        privacy policy
                      </Link>
                      .
                    </>
                  ) : (
                    <>
                      J&apos;ai lu et j&apos;accepte la{" "}
                      <Link to="/legal/privacidad" target="_blank" className="underline text-terracotta hover:text-ivory">
                        politique de confidentialité
                      </Link>
                      .
                    </>
                  )}
                </span>
              </label>

              <button type="submit" disabled={loading || !gdprAccepted} data-testid="contact-submit" className="self-start mt-6 bg-terracotta text-ivory px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase hover:bg-ivory hover:text-olive transition-all duration-500 hover:tracking-[0.32em] disabled:opacity-40 disabled:cursor-not-allowed">
                {loading ? c.sending : c.send}
              </button>
            </form>
          )}
        </Reveal>
        </div>
      </div>
    </section>
  );
}
